import { AdminAuthProvider, useAdminAuth } from "@auth/AuthContext"
import { GuestRoute } from "@auth/GuestRoute"
import { ProtectedRoute } from "@auth/ProtectedRoute"
import { AppShell } from "@components/AppShell"
import { buttonClasses } from "@components/ui/Button"
import { EmptyState } from "@components/ui/EmptyState"
import { PageLoader } from "@components/ui/PageLoader"
import { ToastProvider } from "@components/ui/Toaster"
import { ThemeProvider } from "@features/settings"
import { InstitutionScopeProvider } from "@hooks/useInstitutionScope"
import { LoginPage } from "@pages/Login"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Suspense, lazy, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { BrowserRouter, Link, Navigate, Route, Routes, useParams } from "react-router-dom"

import { isPlatformSuperuser } from "@/constants/roles"
import { ROUTES } from "@/constants/routes"

const OverviewPage = lazy(() =>
  import("@features/overview").then((m) => ({ default: m.OverviewPage })),
)
const InstitutionsPage = lazy(() =>
  import("@features/institutions").then((m) => ({ default: m.InstitutionsPage })),
)
const OnboardingWizardPage = lazy(() =>
  import("@features/onboarding").then((m) => ({ default: m.OnboardingWizardPage })),
)
const SchoolsPage = lazy(() =>
  import("@features/schools").then((m) => ({ default: m.SchoolsPage })),
)
// PRD-56 PR (4) — detail pages.
const SchoolDetailPage = lazy(() =>
  import("@features/schools").then((m) => ({ default: m.SchoolDetailPage })),
)
const AddProfessorsPage = lazy(() =>
  import("@features/professors").then((m) => ({ default: m.AddProfessorsPage })),
)
const ProfessorDetailPage = lazy(() =>
  import("@features/professors").then((m) => ({ default: m.ProfessorDetailPage })),
)
const InstitutionDetailPage = lazy(() =>
  import("@features/institutions").then((m) => ({ default: m.InstitutionDetailPage })),
)
const MyInstitutionPage = lazy(() =>
  import("@features/institutions").then((m) => ({ default: m.MyInstitutionPage })),
)
const ProfessorsPage = lazy(() =>
  import("@features/professors").then((m) => ({ default: m.ProfessorsPage })),
)
const AdminUsersPage = lazy(() =>
  import("@features/admin-users").then((m) => ({ default: m.AdminUsersPage })),
)
// PRD-69 / ADR-0053 — the "Materiales" tab: Platonica prepares, sends and
// grades inside a consenting professor's account. One chunk for the feature.
const StaffMaterialsPage = lazy(() =>
  import("@features/staff-materials").then((m) => ({ default: m.StaffMaterialsPage })),
)
const NewStaffMaterialPage = lazy(() =>
  import("@features/staff-materials").then((m) => ({ default: m.NewStaffMaterialPage })),
)
const StaffMaterialDetailPage = lazy(() =>
  import("@features/staff-materials").then((m) => ({ default: m.StaffMaterialDetailPage })),
)
const StaffUploadPage = lazy(() =>
  import("@features/staff-materials").then((m) => ({ default: m.StaffUploadPage })),
)
const SettingsPage = lazy(() =>
  import("@features/settings").then((m) => ({ default: m.SettingsPage })),
)
// PRD-33 / ADR-0030 — public account-activation (set-password) page. Lives
// outside <ProtectedRoute>/<AppShell> on purpose: a newly-provisioned admin
// has no session yet and authenticates via the URL token. Lazy so the chunk
// only loads when an invitee opens the link.
const ActivarPage = lazy(() =>
  import("@features/account-activation").then((m) => ({ default: m.ActivarPage })),
)
// PRD-33 / ADR-0030 — public password-reset (set-new-password) page. Same
// token state machine + endpoints as activation, reset-flavored copy.
const RestablecerPage = lazy(() =>
  import("@features/account-activation").then((m) => ({ default: m.RestablecerPage })),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false },
  },
})

// Cross-institution "Instituciones" + onboarding are platform-operator surfaces;
// an ordinary (single-institution) admin or viewer who hits the URL directly
// sees an explicit "no access" state rather than a silent bounce to the
// dashboard (PLA-86 / PRD-32 §FR-18). Backend enforces this server-side too.
function PlatformSuperuserRoute({ children }: { children: ReactNode }) {
  const { admin } = useAdminAuth()
  const { t } = useTranslation("common")
  return isPlatformSuperuser(admin?.role) ? (
    <>{children}</>
  ) : (
    <EmptyState title={t("forbidden.title")} message={t("forbidden.message")} />
  )
}

/**
 * The "Materiales" tab is for the sessions the server granted it to — a
 * platform superuser with the feature on. Anyone else who follows a link sees
 * the same "no access" state; the API refuses them regardless (PRD-69 §FR-1).
 */
function StaffMaterialsRoute({ children }: { children: ReactNode }) {
  const { admin } = useAdminAuth()
  const { t } = useTranslation("common")
  return admin?.can_manage_staff_materials ? (
    <>{children}</>
  ) : (
    <EmptyState title={t("forbidden.title")} message={t("forbidden.message")} />
  )
}

/**
 * `/instituciones/:id` is a staff page. A director who follows a link to
 * their own institution lands on "Mi institución"; anyone else's reads as
 * no access, like the rest of the staff surface.
 */
function InstitutionDetailRoute() {
  const { admin } = useAdminAuth()
  const { institutionId } = useParams()
  if (isPlatformSuperuser(admin?.role)) return <InstitutionDetailPage />
  if (institutionId === admin?.institution.id) {
    return <Navigate to={ROUTES.myInstitution} replace />
  }
  return (
    <PlatformSuperuserRoute>
      <InstitutionDetailPage />
    </PlatformSuperuserRoute>
  )
}

function NotFoundPage() {
  const { t } = useTranslation("common")
  return (
    <EmptyState
      title={t("notfound.title")}
      message={t("notfound.message")}
      action={
        <Link to={ROUTES.overview} className={buttonClasses()}>
          {t("notfound.back")}
        </Link>
      }
    />
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AdminAuthProvider>
            <InstitutionScopeProvider>
              <ToastProvider>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route
                      path={ROUTES.login}
                      element={
                        <GuestRoute>
                          <LoginPage />
                        </GuestRoute>
                      }
                    />
                    {/* PRD-33 / ADR-0030 — account-activation set-password page.
                    Public, token-authenticated, NOT wrapped in
                    <ProtectedRoute>/<AppShell>. */}
                    <Route path={ROUTES.activate} element={<ActivarPage />} />
                    {/* PRD-33 / ADR-0030 — password-reset set-new-password page.
                    Public sibling of /activar; reset-flavored copy. */}
                    <Route path={ROUTES.passwordReset} element={<RestablecerPage />} />
                    <Route
                      element={
                        <ProtectedRoute>
                          <AppShell />
                        </ProtectedRoute>
                      }
                    >
                      <Route path={ROUTES.overview} element={<OverviewPage />} />
                      <Route
                        path={ROUTES.institutions}
                        element={
                          <PlatformSuperuserRoute>
                            <InstitutionsPage />
                          </PlatformSuperuserRoute>
                        }
                      />
                      <Route
                        path={ROUTES.institutionNew}
                        element={
                          <PlatformSuperuserRoute>
                            <OnboardingWizardPage />
                          </PlatformSuperuserRoute>
                        }
                      />
                      <Route path={ROUTES.institution} element={<InstitutionDetailRoute />} />
                      <Route path={ROUTES.myInstitution} element={<MyInstitutionPage />} />
                      <Route path={ROUTES.schools} element={<SchoolsPage />} />
                      <Route path={ROUTES.school} element={<SchoolDetailPage />} />
                      <Route path={ROUTES.professors} element={<ProfessorsPage />} />
                      <Route path={ROUTES.professorsAdd} element={<AddProfessorsPage />} />
                      <Route path={ROUTES.professor} element={<ProfessorDetailPage />} />
                      <Route path={ROUTES.adminUsers} element={<AdminUsersPage />} />
                      <Route
                        path={ROUTES.staffMaterialNew}
                        element={
                          <StaffMaterialsRoute>
                            <NewStaffMaterialPage />
                          </StaffMaterialsRoute>
                        }
                      />
                      <Route
                        path={ROUTES.staffMaterial}
                        element={
                          <StaffMaterialsRoute>
                            <StaffMaterialDetailPage />
                          </StaffMaterialsRoute>
                        }
                      />
                      <Route
                        path={ROUTES.staffUpload}
                        element={
                          <StaffMaterialsRoute>
                            <StaffUploadPage />
                          </StaffMaterialsRoute>
                        }
                      />
                      <Route
                        path={ROUTES.staffMaterials}
                        element={
                          <StaffMaterialsRoute>
                            <StaffMaterialsPage />
                          </StaffMaterialsRoute>
                        }
                      />
                      <Route path={ROUTES.settings} element={<SettingsPage />} />
                      {/* PRD-60 chunk B renamed the section back to "Usuarios";
                      keep the /administradores bookmark working for one release. */}
                      <Route
                        path="/administradores"
                        element={<Navigate to={ROUTES.adminUsers} replace />}
                      />
                      {/* Keep the old superadmin bookmark working for one release. */}
                      <Route
                        path="/organizaciones"
                        element={<Navigate to={ROUTES.institutions} replace />}
                      />
                      <Route path="*" element={<NotFoundPage />} />
                    </Route>
                  </Routes>
                </Suspense>
              </ToastProvider>
            </InstitutionScopeProvider>
          </AdminAuthProvider>
        </BrowserRouter>
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}
